import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { getCurrentUser as apiGetCurrentUser, loginSessionCookie, logoutSessionCookie, checkHealth } from '../utils/api';

const AuthContext = createContext(null);

const debugRefresh = () => typeof window !== 'undefined' && localStorage.getItem('DEBUG_REFRESH');
const log = (msg, data) => { if (debugRefresh()) console.log('[Refresh]', msg, data !== undefined ? data : ''); };
const logAuth = (msg, data) => { console.log('[Auth]', msg, data !== undefined ? data : ''); };

// ── Device-trust helpers for "Remember this device" on 2FA ────────────────────
// Stores a per-user trust entry in localStorage that expires after 30 days.
// Trust persists across explicit sign-outs (by design: "remember this device"
// should survive a logout so the user isn't re-challenged immediately on next login).
const MFA_TRUST_PREFIX = 'mfa_trusted_device_';
const MFA_TRUST_DAYS = 30;

function isMFATrustedDevice(userId) {
  try {
    const raw = localStorage.getItem(`${MFA_TRUST_PREFIX}${userId}`);
    if (!raw) return false;
    const { expiresAt } = JSON.parse(raw);
    return Date.now() < expiresAt;
  } catch { return false; }
}

function setMFADeviceTrust(userId) {
  try {
    localStorage.setItem(`${MFA_TRUST_PREFIX}${userId}`, JSON.stringify({
      expiresAt: Date.now() + MFA_TRUST_DAYS * 24 * 60 * 60 * 1000,
    }));
  } catch { /* ignore quota/private-mode errors */ }
}
// ─────────────────────────────────────────────────────────────────────────────

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [supabaseUser, setSupabaseUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // True when auth/me is retrying due to cold-start gateway errors — lets UI show a helpful message.
  const [serverWarmingUp, setServerWarmingUp] = useState(false);
  const initialLoadInProgressRef = useRef(true);
  // When true, login() is mid-flight and will complete auth itself after MFA check;
  // onAuthStateChange should skip auto-refreshUserProfile for that SIGNED_IN event.
  const mfaPendingRef = useRef(false);
  const deferredAuthSyncTimerRef = useRef(null);

  // Initialize auth state on mount. Wait for INITIAL_SESSION or SIGNED_IN so we don't miss session
  // restored from localStorage (on refresh Supabase often fires SIGNED_IN, not INITIAL_SESSION).
  useEffect(() => {
    // Allow time for backend cold start (e.g. Railway); keep above FETCH_TIMEOUT_MS in api.js.
    const LOADING_TIMEOUT_MS = 45000;
    const INITIAL_SESSION_WAIT_MS = 5000;

    let resolveInitial = null;
    const initialSessionPromise = new Promise((resolve) => {
      resolveInitial = resolve;
    });
    const sessionWithTimeout = Promise.race([
      initialSessionPromise,
      new Promise((r) => setTimeout(() => r(null), INITIAL_SESSION_WAIT_MS)),
    ]);
    let disposed = false;
    const deferAuthSync = (session) => {
      clearTimeout(deferredAuthSyncTimerRef.current);
      deferredAuthSyncTimerRef.current = setTimeout(async () => {
        deferredAuthSyncTimerRef.current = null;
        if (disposed || !session?.user || mfaPendingRef.current) return;
        // Supabase holds an internal auth lock while onAuthStateChange runs. Any
        // Supabase-backed API work must happen after that callback has returned.
        await refreshUserProfile();
        if (disposed) return;
        try {
          await loginSessionCookie(session.access_token);
        } catch (e) {
          if (debugRefresh()) console.warn('[Refresh] cookie-login failed:', e);
        }
      }, 0);
    };


    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        logAuth('onAuthStateChange', { event, hasSession: !!session?.user });
        log('onAuthStateChange', { event, hasSession: !!session?.user });
        if (event === 'INITIAL_SESSION') {
          logAuth('INITIAL_SESSION received', { hasSession: !!session?.user });
          if (resolveInitial) {
            resolveInitial(session ?? null);
            resolveInitial = null;
          }
          if (session?.user) setSupabaseUser(session.user);
          return;
        }
        // On refresh Supabase often fires SIGNED_IN with restored session; resolve wait so initAuth gets session.
        if (session?.user && resolveInitial) {
          logAuth('SIGNED_IN resolving initial wait', { hasSession: true });
          resolveInitial(session);
          resolveInitial = null;
        }
        if (session?.user) {
          setSupabaseUser(session.user);
          if (!initialLoadInProgressRef.current) {
            // Skip auto-refresh when login() is handling MFA; it will call refreshUserProfile()
            // itself once the TOTP challenge is resolved (or device trust bypasses it).
            if (mfaPendingRef.current) return;
            deferAuthSync(session);
          }
        } else {
          setSupabaseUser(null);
          setUser(null);
        }
      }
    );

    const initAuth = async (isRetry = false) => {
      logAuth('initAuth start', { isRetry, path: typeof window !== 'undefined' ? window.location.pathname : '' });
      log('initAuth start', { isRetry });
      try {
        logAuth('waiting for INITIAL_SESSION or timeout...');
        let session = await sessionWithTimeout;
        logAuth('session after wait', { hasSession: !!session?.user });
        log('session after INITIAL_SESSION wait', { hasSession: !!session?.user });

        // OAuth callback: tokens in URL hash; Supabase may not have applied them to INITIAL_SESSION yet.
        const isOAuthCallback =
          typeof window !== 'undefined' &&
          (window.location.hash?.includes('access_token') || window.location.hash?.includes('refresh_token'));
        if (!session?.user && isOAuthCallback) {
          const maxWait = 5000;  // Increased from 2500ms for slower networks
          const step = 300;      // Slightly longer interval to reduce CPU usage
          for (let elapsed = 0; elapsed < maxWait; elapsed += step) {
            await new Promise((r) => setTimeout(r, step));
            const { data } = await supabase.auth.getSession();
            session = data?.session;
            if (session?.user) break;
          }
          if (session?.user && window.history.replaceState) {
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
          }
        }

        if (session?.user) {
          setSupabaseUser(session.user);
          logAuth('calling refreshUserProfile (GET /api/auth/me)...');
          log('calling refreshUserProfile (GET /api/auth/me)');
          await refreshUserProfile();
          logAuth('refreshUserProfile done');
          log('refreshUserProfile done');
          try {
            await loginSessionCookie(session.access_token);
          } catch (e) {
            if (debugRefresh()) console.warn('[Refresh] cookie-login failed:', e);
          }
        } else {
          logAuth('no Supabase session, trying auth/me with cookie only');
          log('no Supabase session, trying auth/me with cookie');
          await refreshUserProfile();
        }
      } catch (e) {
        logAuth('initAuth catch', { name: e?.name, message: e?.message, code: e?.code });
        if (e?.name === 'AbortError' && !isRetry) {
          log('AbortError, retrying initAuth');
          await new Promise((r) => setTimeout(r, 150));
          return initAuth(true);
        }
        console.error('[Auth] initAuth error:', e);
      } finally {
        initialLoadInProgressRef.current = false;
        setLoading(false);
        logAuth('initAuth finally, loading=false');
        log('initAuth finished, loading=false');
      }
    };

    // Fire-and-forget health ping immediately to wake a cold Railway server before the
    // more expensive /api/auth/me request fires. Non-fatal — ignore any errors.
    checkHealth().catch(() => {});

    logAuth('starting initAuth + loading timeout', { LOADING_TIMEOUT_MS, INITIAL_SESSION_WAIT_MS });
    const timeoutId = setTimeout(() => {
      logAuth('LOADING_TIMEOUT fired, forcing loading=false');
      setLoading(false);
    }, LOADING_TIMEOUT_MS);
    initAuth().finally(() => {
      clearTimeout(timeoutId);
      setServerWarmingUp(false);
      setLoading(false);
      logAuth('initAuth().finally ran');
    });

    return () => {
      disposed = true;
      clearTimeout(deferredAuthSyncTimerRef.current);
      deferredAuthSyncTimerRef.current = null;
      subscription?.unsubscribe();
    };
  }, []);

  // Fetch user profile from backend (retries on 401/timeout/network to handle cold start and token race)
  const refreshUserProfile = useCallback(async () => {
    logAuth('refreshUserProfile called');
    setError(null);
    const tryFetch = async () => {
      logAuth('refreshUserProfile tryFetch: GET /api/auth/me');
      const response = await apiGetCurrentUser();
      logAuth('refreshUserProfile tryFetch: got response', { hasUser: !!response?.user });
      setUser(response.user);
    };
    const isUnauthorized = (err) => err?.code === 'UNAUTHORIZED' || (err?.message && String(err.message).includes('401'));
    const isBadResponse = (err) => err?.code === 'BAD_RESPONSE'; // HTML instead of JSON - don't retry
    const isTimeout = (err) => err?.code === 'TIMEOUT' || err?.name === 'AbortError';
    const isBadGateway = (err) => err?.code === 'BAD_GATEWAY' || (err?.message && /50[234]/.test(String(err.message)));
    const isNetworkError = (err) => err?.message?.includes('fetch') || err?.message?.includes('Failed to fetch') || err?.message?.includes('NetworkError');
    const isRetryable = (err) => !isBadResponse(err) && (isTimeout(err) || isBadGateway(err) || isNetworkError(err));

    try {
      await tryFetch();
      setServerWarmingUp(false);
      logAuth('refreshUserProfile: success on first attempt');
      log('refreshUserProfile: success on first attempt');
    } catch (e) {
      logAuth('refreshUserProfile first attempt failed', { message: e?.message, code: e?.code });
      // Don't retry on 401 or BAD_RESPONSE (HTML) - fail fast
      if (isUnauthorized(e)) {
        logAuth('refreshUserProfile: 401, not retrying');
        setServerWarmingUp(false);
        setUser(null);
        setSupabaseUser(null);
        setError('Your session is not authorized. Please log in again.');
        return;
      }
      if (isBadResponse(e)) {
        logAuth('refreshUserProfile: BAD_RESPONSE (HTML), not retrying');
        setServerWarmingUp(false);
        setUser(null);
        setError(`API routing returned HTML for ${e.url || '/api/auth/me'}. Check the /api rewrite/proxy configuration.`);
        return;
      }

      // Surface warm-up message to the UI before retrying
      if (isTimeout(e) || isBadGateway(e)) {
        setServerWarmingUp(true);
      }

      // First retry with appropriate delay
      const delay1 = (isTimeout(e) || isBadGateway(e)) ? 2000 : 600;
      logAuth('refreshUserProfile: retry 1 after', delay1, 'ms');
      log('refreshUserProfile: retry 1 after', delay1, 'ms');
      await new Promise((r) => setTimeout(r, delay1));

      try {
        await tryFetch();
        setServerWarmingUp(false);
        log('refreshUserProfile: success on retry 1');
        return;
      } catch (retry1Err) {
        if (isUnauthorized(retry1Err)) {
          log('refreshUserProfile: 401 on retry 1');
          setServerWarmingUp(false);
          setUser(null);
          setSupabaseUser(null);
          return;
        }

        // Second retry with longer delay (backend cold start)
        if (isRetryable(retry1Err)) {
          const delay2 = (isTimeout(retry1Err) || isBadGateway(retry1Err)) ? 3500 : 1500;
          log('refreshUserProfile: retry 2 after', delay2, 'ms');
          await new Promise((r) => setTimeout(r, delay2));

          try {
            await tryFetch();
            setServerWarmingUp(false);
            log('refreshUserProfile: success on retry 2');
            return;
          } catch (retry2Err) {
            setServerWarmingUp(false);
            if (isUnauthorized(retry2Err)) {
              log('refreshUserProfile: 401 on retry 2');
              setUser(null);
              setSupabaseUser(null);
            } else {
              console.error('Failed to fetch user profile (after retries):', retry2Err);
            }
            return;
          }
        }

        // Non-retryable error on first retry
        setServerWarmingUp(false);
        console.error('Failed to fetch user profile (non-retryable):', retry1Err);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Email/password login — with 2FA (TOTP) and "remember this device" support.
  // Sets mfaPendingRef=true BEFORE signInWithPassword so the SIGNED_IN event handler
  // (fired async by Supabase) sees the flag and skips auto-refreshUserProfile.
  const login = async (email, password) => {
    setError(null);
    mfaPendingRef.current = true;
    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        mfaPendingRef.current = false;
        throw new Error(signInError.message);
      }

      if (data.user) {
        setSupabaseUser(data.user);

        // Check whether the user's Supabase session requires an AAL2 TOTP step.
        const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (aalData?.nextLevel === 'aal2') {
          // Device trust: if this browser was previously trusted, skip the TOTP prompt.
          if (isMFATrustedDevice(data.user.id)) {
            logAuth('login: device trusted, skipping MFA prompt');
            mfaPendingRef.current = false;
            await refreshUserProfile();
            try { await loginSessionCookie(data.session?.access_token); } catch {}
            return { success: true };
          }
          // Prompt the user for TOTP. Resolve the enrolled factor ID so Login.js can pass it to verifyMFA().
          const { data: factorsData } = await supabase.auth.mfa.listFactors();
          const totpFactor = factorsData?.totp?.find(f => f.status === 'verified');
          logAuth('login: MFA required', { factorId: totpFactor?.id });
          // mfaPendingRef stays true — verifyMFA() will clear it after TOTP succeeds.
          return { success: false, mfaRequired: true, factorId: totpFactor?.id };
        }

        mfaPendingRef.current = false;
        await refreshUserProfile();
        try { await loginSessionCookie(data.session?.access_token); } catch {}
        return { success: true };
      }

      mfaPendingRef.current = false;
    } catch (err) {
      mfaPendingRef.current = false;
      setError(err.message || 'Login failed');
      return { success: false, error: err.message };
    }
  };

  // Verify a TOTP code after login(). Called from Login.js when mfaRequired=true.
  // rememberDevice=true stores a 30-day device-trust token in localStorage so the user
  // won't be prompted for TOTP again on this browser/device for that period.
  const verifyMFA = async (factorId, code, rememberDevice = false) => {
    try {
      const { data: challenge, error: ce } = await supabase.auth.mfa.challenge({ factorId });
      if (ce) throw new Error(ce.message);
      const { data: verifyData, error: ve } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
      });
      if (ve) throw new Error(ve.message);
      if (rememberDevice && supabaseUser?.id) {
        setMFADeviceTrust(supabaseUser.id);
        logAuth('verifyMFA: device trust stored', { userId: supabaseUser.id });
      }
      mfaPendingRef.current = false;
      await refreshUserProfile();
      const session = verifyData?.session;
      if (session?.access_token) {
        try { await loginSessionCookie(session.access_token); } catch {}
      }
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // Email/password signup
  const signup = async (email, password, name) => {
    setError(null);
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: name },
          emailRedirectTo: typeof window !== 'undefined' ? `${window.location.origin}/profile` : undefined,
        },
      });

      if (signUpError) {
        throw new Error(signUpError.message);
      }

      if (data.user) {
        setSupabaseUser(data.user);
        // Backend will create profile on first /api/auth/me call
        await refreshUserProfile();
        const needsConfirmation = !data.user.email_confirmed_at;
        return { success: true, needsConfirmation };
      }
    } catch (err) {
      setError(err.message || 'Signup failed');
      return { success: false, error: err.message };
    }
  };

  // Google OAuth login
  const googleLogin = async () => {
    setError(null);
    try {
      const { data, error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/profile`,
        },
      });

      if (oauthError) {
        throw new Error(oauthError.message);
      }

      // OAuth redirect happens, user will be back after authentication
      return { success: true };
    } catch (err) {
      setError(err.message || 'Google login failed');
      return { success: false, error: err.message };
    }
  };

  // Logout: clear UI state first, then HTTP-only cookie and Supabase session. Callers should
  // await then navigate so redirect happens after cleanup.
  const logout = useCallback(async () => {
    setUser(null);
    setSupabaseUser(null);
    setError(null);
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.error('Supabase signOut error:', e);
    }
    try {
      await logoutSessionCookie();
    } catch (e) {
      if (debugRefresh()) console.warn('Cookie logout failed:', e);
    }
  }, []);

  // Update usage
  const updateUsage = useCallback((queriesUsed) => {
    if (user) {
      const updatedUser = {
        ...user,
        queries_used: queriesUsed
      };
      setUser(updatedUser);
    }
  }, [user]);

  // Resend email confirmation (Supabase: type 'signup' sends the verification link again)
  const resendConfirmationEmail = useCallback(async () => {
    const email = user?.email || supabaseUser?.email;
    if (!email) return { success: false, error: 'No email address' };
    try {
      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email,
      });
      if (resendError) throw new Error(resendError.message);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message || 'Failed to resend' };
    }
  }, [user?.email, supabaseUser?.email]);

  // Get auth token for API calls
  const getToken = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token;
  }, []);

  // Re-check session and load profile (for "Try again" on dashboard when initial load failed or timed out)
  const retryAuthOrProfile = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      setSupabaseUser(session.user);
      await refreshUserProfile();
    }
  }, []);

  const value = {
    user,
    supabaseUser,
    login,
    signup,
    googleLogin,
    logout,
    updateUsage,
    refreshUserProfile,
    retryAuthOrProfile,
    resendConfirmationEmail,
    getToken,
    verifyMFA,
    isAuthenticated: !!user,
    loading,
    serverWarmingUp,
    error,
    clearError: () => setError(null)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
